import React, { useEffect, useState } from "react";
import { Loader2, Download, Package, FileText } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import api, { API, formatMoney, formatMoney2, formatNumber } from "../lib/api";
import { useSortable, SortHeader } from "../lib/sort";
import { exportToPdf } from "../lib/exports";
import { col } from "../lib/exportCols";
import { useHistoryBack } from "../lib/useHistoryBack";
import { VolverButton } from "./VolverButton";

const fd = (d) => (d ? new Date(d).toLocaleDateString("es-ES") : "—");

function Filtro({ label, value, onChange, options, testid }) {
  return (
    <label className="text-xs text-slate-500 flex flex-col gap-1">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input text-sm py-1.5" data-testid={testid}>
        <option value="">Todos</option>
        {options.map((o) => <option key={o.v ?? o} value={o.v ?? o}>{o.l ?? o}</option>)}
      </select>
    </label>
  );
}

export function CompradoSinVentaPanel({ open, onOpenChange }) {
  const [f, setF] = useState({ familia: "", laboratorio: "", segment: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const sort = useSortable(data?.items || [], "valor_pvp", "desc");
  const [opts, setOpts] = useState({ familias: [], laboratorios: [] });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/inventory/bought-last-month/products", { params })
      .then((r) => { setData(r.data); setOpts({ familias: r.data.familias, laboratorios: r.data.laboratorios }); })
      .catch(() => { setData(null); toast.error("No se pudo cargar la lista. Inténtalo de nuevo."); })
      .finally(() => setLoading(false));
  }, [open, f]);

  const exportar = () => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v))).toString();
    window.open(`${API}/inventory/bought-last-month/export${qs ? "?" + qs : ""}`, "_blank");
  };
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const nav = useHistoryBack(open, () => onOpenChange(false), { escape: false });

  return (
    <Sheet open={open} onOpenChange={(v) => (v ? onOpenChange(true) : nav.close())}>
      <SheetContent side="right" className="w-full sm:max-w-none p-0 flex flex-col bg-white z-[70]" data-testid="comprado-sin-venta-panel">
        <SheetHeader className="px-6 py-5 border-b border-slate-200 text-left flex-row items-start gap-4 space-y-0">
          <VolverButton onClick={nav.back} testid="csv-volver" className="mt-1" />
          <div>
          <SheetTitle className="font-display text-2xl font-extrabold tracking-tight">Comprado el último mes y sin ventas</SheetTitle>
          <SheetDescription>
            {data ? <>Entradas del {fd(data.desde)} al {fd(data.hasta)} que no se han vendido desde la compra · {data.study_name}</> : "Cargando…"}
          </SheetDescription>
          </div>
        </SheetHeader>
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <Filtro label="Canal" value={f.segment} onChange={set("segment")} testid="csv-filter-segment"
                  options={[{ v: "farmacia", l: "Farmacia" }, { v: "parafarmacia", l: "Parafarmacia" }]} />
          <Filtro label="Familia" value={f.familia} onChange={set("familia")} options={opts.familias} testid="csv-filter-familia" />
          <Filtro label="Laboratorio" value={f.laboratorio} onChange={set("laboratorio")} options={opts.laboratorios} testid="csv-filter-laboratorio" />
          <div className="flex gap-2">
            <button onClick={exportar} className="btn btn-primary btn-sm" data-testid="csv-export-btn"><Download size={14} /> Excel</button>
            <button disabled={!data?.items?.length} className="btn btn-outline btn-sm" data-testid="csv-pdf-btn"
                    onClick={() => exportToPdf({ filename: "comprado-sin-venta", title: "Comprado en el último mes y sin ventas", orientation: "landscape",
                      subtitle: `Entradas del ${fd(data.desde)} al ${fd(data.hasta)} · ${data.study_name}`, rows: sort.sortedRows,
                      columns: [col.text("CN", "cn"), col.text("Descripción", "descripcion"), col.text("Familia", "familia"), col.text("Laboratorio", "laboratorio"),
                                col.num("Stock", "stock"), col.eur("Valor PVP", "valor_pvp"), col.date("Entrada", "u_entrada"), col.date("Última venta", "u_salida")] })}>
              <FileText size={14} /> PDF
            </button>
          </div>
        </div>
        <div className="px-6 py-3 flex gap-8 border-b border-slate-200">
          <div><div className="kpi-label">Productos</div><div className="font-display text-xl font-extrabold" data-testid="csv-total">{formatNumber(data?.total || 0)}</div></div>
          <div><div className="kpi-label">Valor stock PVP</div><div className="font-display text-xl font-extrabold text-amber-600" data-testid="csv-valor">{formatMoney(data?.valor_pvp || 0)}</div></div>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Cargando productos…</div>
          ) : !data?.items?.length ? (
            <div className="p-12 text-center text-slate-500"><Package size={28} className="mx-auto text-slate-400" /><p className="mt-2">No hay productos con estos filtros.</p></div>
          ) : (
            <table className="dense" data-testid="csv-table">
              <thead><tr>
                <th><SortHeader label="CN" k="cn" sort={sort} defaultDir="asc" /></th>
                <th><SortHeader label="Descripción" k="descripcion" sort={sort} defaultDir="asc" /></th>
                <th><SortHeader label="Familia" k="familia" sort={sort} defaultDir="asc" /></th>
                <th><SortHeader label="Laboratorio" k="laboratorio" sort={sort} defaultDir="asc" /></th>
                <th className="num"><SortHeader label="Stock" k="stock" sort={sort} num /></th>
                <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={sort} num /></th>
                <th className="num"><SortHeader label="Uds vend." k="uds_vendidas" sort={sort} num /></th>
                <th><SortHeader label="Entrada" k="u_entrada" sort={sort} /></th>
                <th><SortHeader label="Última venta" k="u_salida" sort={sort} /></th>
              </tr></thead>
              <tbody>
                {sort.sortedRows.slice(0, 500).map((p) => (
                  <tr key={p.cn}>
                    <td className="font-mono">{p.cn}</td>
                    <td>{p.descripcion}</td>
                    <td className="text-xs">{p.familia}</td>
                    <td className="text-xs">{p.laboratorio}</td>
                    <td className="num">{formatNumber(p.stock)}</td>
                    <td className="num font-semibold">{formatMoney2(p.valor_pvp)}</td>
                    <td className="num">{formatNumber(p.uds_vendidas || 0)}</td>
                    <td className="text-xs font-mono whitespace-nowrap">{fd(p.u_entrada)}</td>
                    <td className="text-xs font-mono whitespace-nowrap">{fd(p.u_salida)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data?.items?.length > 500 && <div className="px-6 py-3 text-xs text-slate-500 bg-slate-50 border-t">Se muestran 500 de {formatNumber(data.total)}. El Excel incluye todos.</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
